import PageShell from "@/components/PageShell";
import { usePageContent } from "@/hooks/usePageContent";
import { useSEO } from "@/hooks/useSEO";
import { Loader2 } from "lucide-react";

/**
 * CMSPage - Generic CMS-driven page component
 * Reads content from page_content collection based on page_id
 * 
 * @param {string} pageId - The page_id to fetch from page_content
 * @param {string} defaultTitle - Default title if CMS content not found
 * @param {string} defaultDescription - Default description
 * @param {React.Component} icon - Optional icon component
 * @param {React.Component} children - Optional custom content sections
 */
export default function CMSPage({ 
  pageId, 
  defaultTitle, 
  defaultDescription,
  icon: Icon,
  children 
}) {
  const { content: c, loading } = usePageContent(pageId);

  // SEO
  useSEO({
    seo_title: c?.seo_title || `${defaultTitle} | SellLocalHomes`,
    seo_description: c?.seo_description || defaultDescription
  });

  if (loading) {
    return (
      <PageShell>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="pt-12 md:pt-16 pb-16 px-6 bg-gradient-to-br from-primary/10 via-primary/5 to-background">
          <div className="max-w-5xl mx-auto">
            {Icon && (
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-6">
                <Icon className="h-10 w-10 text-primary" />
              </div>
            )}
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              {c?.hero_title || defaultTitle}
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-3xl">
              {c?.hero_description || defaultDescription}
            </p>
          </div>
        </section>

        {/* Main Content Section */}
        <section className="py-16 px-6">
          <div className="max-w-5xl mx-auto">
            {c?.section_1_title && (
              <div className="mb-12">
                <h2 className="font-serif text-3xl font-bold mb-4">{c.section_1_title}</h2>
                <div className="prose prose-lg max-w-none text-muted-foreground">
                  <p>{c.section_1_content}</p>
                </div>
              </div>
            )}

            {c?.section_2_title && (
              <div className="mb-12">
                <h2 className="font-serif text-3xl font-bold mb-4">{c.section_2_title}</h2>
                <div className="prose prose-lg max-w-none text-muted-foreground">
                  <p>{c.section_2_content}</p>
                </div>
              </div>
            )}

            {c?.section_3_title && (
              <div className="mb-12">
                <h2 className="font-serif text-3xl font-bold mb-4">{c.section_3_title}</h2>
                <div className="prose prose-lg max-w-none text-muted-foreground">
                  <p>{c.section_3_content}</p>
                </div>
              </div>
            )}

            {/* Custom children content */}
            {children}

            {/* CTA Section */}
            {c?.cta_title && (
              <div className="mt-16 text-center py-12 px-8 bg-muted/30 border border-border rounded-lg">
                <h2 className="font-serif text-2xl font-bold mb-3">{c.cta_title}</h2>
                <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">{c.cta_description}</p>
                {c?.cta_button_text && (
                  <a 
                    href={c?.cta_button_link || '/contact'}
                    className="inline-block bg-primary text-white px-8 py-3 font-medium hover:bg-primary/90 transition-colors"
                  >
                    {c.cta_button_text}
                  </a>
                )}
              </div>
            )}
          </div>
        </section>
      </div>
    </PageShell>
  );
}
